import { useMemo } from "react";
import { IconSettings } from "../icons";
import { ReorderableMultiSelect } from "../reorderable-multi-select";

interface ColumnOption {
  id: string;
  label: string;
  hidden?: boolean;
  disabled?: boolean;
}

interface ColumnMenuProps {
  columns: ColumnOption[];
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  columnOrder: string[];
  onColumnOrderChange: (order: string[]) => void;
}

export function ColumnMenu({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
}: ColumnMenuProps) {
  const colMap = useMemo(
    () => new Map(columns.map((c) => [c.id, c])),
    [columns],
  );
  const orderedOptions = useMemo(() => {
    return columnOrder
      .filter((id) => colMap.has(id) && !colMap.get(id)?.hidden)
      .map((id) => {
        const col = colMap.get(id) as ColumnOption;
        return {
          label: col.label,
          value: col.id,
          key: col.id,
          disabled: col.disabled,
        };
      });
  }, [colMap, columnOrder]);

  const selectedValues = useMemo(() => {
    return columnOrder
      .filter((id) => !colMap.get(id)?.hidden)
      .filter((id) => columnVisibility[id] !== false);
  }, [colMap, columnOrder, columnVisibility]);

  return (
    <ReorderableMultiSelect
      ariaLabel="Reorder columns"
      options={orderedOptions}
      selectedValues={selectedValues}
      onSelectionChange={(selected) => {
        const selectedSet = new Set(selected);
        const next: Record<string, boolean> = {};
        for (const col of columns) {
          next[col.id] = selectedSet.has(col.id);
        }
        next.details = true;
        onColumnVisibilityChange(next);
      }}
      onOrderChange={(orderedValues) => {
        onColumnOrderChange([...orderedValues, "details"]);
      }}
      noTriggerDisplayText
      chevronIcon={<IconSettings variant="sm" />}
      className="flex gap-2 text-sm"
      dropdownClassName="text-sm right-0"
    />
  );
}
